import React, { FC } from 'react'
import { Tooltip } from 'antd'
import { NOT_AVAILABLE } from 'utils/constants'
import Income from 'components/Income'
import Icon from 'components/Icon'
import { reportedStatusMap, reportedStatusValue } from 'components/ReportedStatus/ReportedStatus'
import VerticalTable from 'UI/VerticalTable/VerticalTable'
import { ITableColumnsType } from 'uikit/tables/default'
import { ESTIMATED_REVENUE_MIN_MULTIPLIER, ESTIMATED_REVENUE_MAX_MULTIPLIER } from 'containers/VerticalPage/str/utils'
import styles from './STRTablePage.module.scss'

interface YearlyBreakdown {
  id: string
  year: string
  estimatedRevenue: number
  reportedRevenue?: number | null
  unreportedRevenue?: number | null
  totalExpenses?: number | null
  expensesRatioPercent?: number | null
  reportedStatus?: reportedStatusValue | null
}

const STATUS_LABELS: Partial<Record<reportedStatusValue, string>> = {
  noFile: 'No File',
  noReport: 'No Report',
  underReport: 'Under Report',
  properReport: 'True Report',
}

const ReportedStatusIcon: FC<{ status?: reportedStatusValue | null }> = ({ status }) => {
  if (!status) return <span>{NOT_AVAILABLE}</span>
  const entry = reportedStatusMap[status]
  if (!entry?.statusIcon) return <span>{NOT_AVAILABLE}</span>
  const label = STATUS_LABELS[status] ?? entry.tooltipValue
  const iconEl = <Icon name={entry.statusIcon} size='regular' />
  return label ? (
    <Tooltip title={label}>
      <span style={{ display: 'inline-flex' }}>{iconEl}</span>
    </Tooltip>
  ) : (
    iconEl
  )
}

export const expandedRowColumns: ITableColumnsType<any> = [
  {
    title: 'Tax Year',
    key: 'year',
    width: 100,
    minWidth: 90,
    sorter: (a: YearlyBreakdown, b: YearlyBreakdown) => Number(a.year) - Number(b.year),
    render: (item: YearlyBreakdown) => item.year,
  },
  {
    title: 'Report Status',
    key: 'reportedStatus',
    width: 110,
    minWidth: 90,
    sorter: (a: YearlyBreakdown, b: YearlyBreakdown) => (a.reportedStatus ?? '').localeCompare(b.reportedStatus ?? ''),
    render: (item: YearlyBreakdown) => <ReportedStatusIcon status={item.reportedStatus} />,
  },
  {
    title: 'Estimated Revenue',
    key: 'estimatedRevenue',
    width: 220,
    minWidth: 200,
    sorter: (a: YearlyBreakdown, b: YearlyBreakdown) => (a.estimatedRevenue ?? 0) - (b.estimatedRevenue ?? 0),
    render: (item: YearlyBreakdown) =>
      typeof item.estimatedRevenue === 'number' ? (
        <span>
          <Income value={item.estimatedRevenue * ESTIMATED_REVENUE_MIN_MULTIPLIER} useCurrencyConversion={true} />
          {' - '}
          <Income value={item.estimatedRevenue * ESTIMATED_REVENUE_MAX_MULTIPLIER} useCurrencyConversion={true} />
        </span>
      ) : (
        NOT_AVAILABLE
      ),
  },
  {
    title: 'Reported Revenue',
    key: 'reportedRevenue',
    width: 160,
    minWidth: 140,
    sorter: (a: YearlyBreakdown, b: YearlyBreakdown) => (a.reportedRevenue ?? 0) - (b.reportedRevenue ?? 0),
    render: (item: YearlyBreakdown) =>
      typeof item.reportedRevenue === 'number' ? (
        <Income value={item.reportedRevenue} useCurrencyConversion={true} />
      ) : (
        NOT_AVAILABLE
      ),
  },
  {
    title: 'UnReported Revenue',
    key: 'unreportedRevenue',
    width: 170,
    minWidth: 150,
    sorter: (a: YearlyBreakdown, b: YearlyBreakdown) => (a.unreportedRevenue ?? 0) - (b.unreportedRevenue ?? 0),
    render: (item: YearlyBreakdown) =>
      typeof item.unreportedRevenue === 'number' ? (
        <Income value={item.unreportedRevenue} useCurrencyConversion={true} />
      ) : (
        NOT_AVAILABLE
      ),
  },
]

export const buildYearlyBreakdown = (record: any): YearlyBreakdown[] => {
  const byYear = record?.totals?.byYear
  const reportedStatusByYear = (record?.entityDetails?.reportedStatus ?? {}) as Record<string, reportedStatusValue>
  if (!byYear && Object.keys(reportedStatusByYear).length === 0) return []

  const estimatedByYear = byYear?.estimated || {}
  const reportedByYear = byYear?.reportedByYear || {}
  const unreportedByYear = byYear?.unReportedByYear || {}
  const expensesByYear = byYear?.expenses || {}

  const allYears = new Set<string>()
  Object.keys(estimatedByYear).forEach(y => allYears.add(y))
  Object.keys(reportedByYear).forEach(y => allYears.add(y))
  Object.keys(unreportedByYear).forEach(y => allYears.add(y))
  Object.keys(expensesByYear).forEach(y => allYears.add(y))
  Object.keys(reportedStatusByYear).forEach(y => allYears.add(y))

  return Array.from(allYears)
    .sort((a, b) => Number(a) - Number(b))
    .filter(year => {
      const est = estimatedByYear[year]
      const exp = expensesByYear[year]
      const hasFinancials = (est !== undefined && est !== 0) || (exp !== undefined && exp !== 0)
      const hasStatus = Boolean(reportedStatusByYear[year])
      return hasFinancials || hasStatus
    })
    .map(year => {
      const est = estimatedByYear[year] ?? 0
      const exp = expensesByYear[year] ?? null
      const ratio = typeof exp === 'number' && est > 0 ? (exp / est) * 100 : null

      return {
        id: `${record.id}-${year}`,
        year,
        estimatedRevenue: est,
        reportedRevenue: reportedByYear[year] ?? null,
        unreportedRevenue: unreportedByYear[year] ?? null,
        totalExpenses: exp,
        expensesRatioPercent: ratio,
        reportedStatus: reportedStatusByYear[year] ?? null,
      }
    })
}

interface STRExpandedRowProps {
  record: any
}

const STRExpandedRow: FC<STRExpandedRowProps> = ({ record }) => {
  const yearlyData = buildYearlyBreakdown(record)

  return (
    <div className={styles.subRowContainer}>
      <VerticalTable columns={expandedRowColumns} dataSource={yearlyData} pageSize={Infinity} />
    </div>
  )
}

export default STRExpandedRow
